/**
 * Turning "make a model out of this zip" into something safe to execute.
 *
 * **The plan is computed in the browser, always.** `buildBasePlan` produces a
 * complete, runnable plan from the archive's own structure plus a deterministic
 * read of what the user typed. The server route (`/ai_assistant/dataset_plan`)
 * only ever REFINES that: better names, a summary in the user's language. Every
 * field it returns goes through `coercePlan` and is merged onto the base, so a
 * field the model omits — or returns out of bounds — keeps the value the
 * browser worked out.
 *
 * That ordering is the whole safety argument, and it is the same discipline the
 * repo already applies to workspace grounding: an enhancement must never turn a
 * working flow into a broken one. It also means the feature works with no
 * `GEMINI_API_KEY` at all, structurally rather than by keeping a second planner
 * alive on a code path nobody exercises.
 *
 * Nothing here talks to the network or touches a File. It is all pure, so the
 * interesting decisions are testable.
 */

import { MAX_IMPORT_IMAGES } from './datasetScan';

export const PLAN_VERSION = 1;

/**
 * The model types an import can create.
 *
 * These are the backend's own strings. `bounding_box` is what the detection
 * collections and the labeling router use — not "detection".
 */
export const SUPPORTED_MODEL_TYPES = Object.freeze(['classification', 'bounding_box']);

export const MIN_CLASSES = 2;
export const MAX_CLASSES = 12;

/** Longest name the UI shows without truncating; also what we send. */
const MAX_NAME_CHARS = 60;

/**
 * `0` is reserved: the backend registers class 0 as `unstructured` and derives
 * `isLabeled` from the name, so a real class landing there is imported as
 * unlabeled data.
 */
const FIRST_CLASS_ID = 1;
const RESERVED_CLASS_NAMES = new Set(['unstructured']);

const NUMBER_WORDS = Object.freeze({
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6,
  seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12,
  // Romanised Hindi/Urdu, because that is how the request arrives here.
  ek: 1, do: 2, teen: 3, tin: 3, char: 4, chaar: 4, paanch: 5, panch: 5,
  chhe: 6, cheh: 6, chah: 6, saat: 7, aath: 8, nau: 9, das: 10,
});

/** Words that mean "a class" in the languages this app is used in. */
const CLASS_NOUN = '(?:classes|class|groups|group|buckets|bucket|categories|category|clusters|cluster|labels|label|개|가지|클래스)';

/**
 * Verb tests are split by script, and they have to be.
 *
 * JavaScript's `\b` is a transition between `\w` and non-`\w`, and Hangul is
 * not `\w` — so `/\b만들/` never matches anywhere, in any string. Putting the
 * Korean verbs inside the Latin alternation silently made the whole Korean path
 * dead: a Korean instruction always read as "no build intent" and fell through
 * to the chat. The boundary is what makes the Latin list safe (so "bana" cannot
 * match inside "banana"), and CJK needs no boundary because it has no such
 * false positives here.
 */
const BUILD_VERBS_LATIN = /\b(?:create|build|make|import|ingest|set\s*up|turn\s+(?:this|it)\s+into|start|generate|bana|banao|banado|banade|bnado|bna|bnao|banaa)/i;
const BUILD_VERBS_CJK = /(?:만들|생성|제작|만드)/;
const DIVIDE_VERBS_LATIN = /\b(?:divide|split|group|cluster|separate|sort|organi[sz]e|classify|bucket|baant|bant|baat|banto|alag)/i;
const DIVIDE_VERBS_CJK = /(?:나눠|나누|분류|그룹)/;
const QUESTION_HEADS_LATIN = /^\s*(?:what|which|how|why|when|where|is|are|can|could|should|does|do i|kya|kaise|kitna|kitne)\b/i;
const QUESTION_HEADS_CJK = /^\s*(?:무엇|어떻게|무슨|뭐)/;

const squash = (text) => String(text || '').replace(/\s+/g, ' ').trim();

const clampInt = (value, low, high, fallback) => {
  const number = Math.round(Number(value));
  if (!Number.isFinite(number)) return fallback;
  return Math.min(high, Math.max(low, number));
};

/** Trim a free-text name to something a Mongo doc and a UI can both hold. */
export const cleanName = (value, fallback = '') => {
  // Anything that is not a string is not a name. Without this a model returning
  // `{}` or `7` yields "[object Object]" / "7" as a project name.
  if (typeof value !== 'string') return fallback;
  const text = squash(value).replace(/^["'`]+|["'`]+$/g, '');
  if (!text) return fallback;
  return text.length > MAX_NAME_CHARS ? text.slice(0, MAX_NAME_CHARS).trimEnd() : text;
};

/**
 * Make a class name safe to store.
 *
 * `unstructured` is the backend's own marker for "not labeled yet", so a folder
 * that happens to carry that name would import its images as UNLABELED — the
 * exact opposite of what a folder-per-class zip means. `_thumbnail` is stripped
 * out of preview URLs by the labeling screen, so a class containing it renders
 * a broken tile.
 */
export const safeClassName = (value, index = 0) => {
  let name = cleanName(value, `class ${index + 1}`);
  if (RESERVED_CLASS_NAMES.has(name.toLowerCase())) name = `${name}-${index + 1}`;
  if (name.toLowerCase().includes('_thumbnail')) name = name.replace(/_thumbnail/gi, '-thumb');
  return name;
};

/** De-duplicate class names in place, because two classes cannot share one. */
export const uniqueClassNames = (names = []) => {
  const seen = new Map();
  return names.map((raw, index) => {
    const base = safeClassName(raw, index);
    const key = base.toLowerCase();
    if (!seen.has(key)) {
      seen.set(key, 1);
      return base;
    }
    const next = seen.get(key) + 1;
    seen.set(key, next);
    return `${base}-${next}`;
  });
};

/**
 * What the user asked for, read deterministically.
 *
 * Returns null when the message reads as a QUESTION rather than an
 * instruction — "what is in this zip?" must reach the normal chat path with the
 * manifest as context, not silently start creating projects.
 */
export const parseIntent = (instruction) => {
  const text = squash(instruction);
  const lower = text.toLowerCase();

  const wantsDivide = DIVIDE_VERBS_LATIN.test(lower) || DIVIDE_VERBS_CJK.test(text);
  const wantsBuild = BUILD_VERBS_LATIN.test(lower) || BUILD_VERBS_CJK.test(text);

  // An empty message alongside an attachment means "do the obvious thing".
  if (!text) return { wantsBuild: true, wantsDivide: false, numClasses: null, maxClasses: null, classNames: null, projectName: null, modelName: null };

  // A question with no imperative verb is a question.
  if (!wantsDivide && !wantsBuild && (QUESTION_HEADS_LATIN.test(lower) || QUESTION_HEADS_CJK.test(text))) return null;
  if (!wantsDivide && !wantsBuild && lower.endsWith('?')) return null;
  if (!wantsDivide && !wantsBuild) return null;

  // "2-4 classes" — a range means "pick the best k in here", which is exactly
  // what /cluster/auto does when num_clusters is omitted.
  let numClasses = null;
  let maxClasses = null;
  const range = lower.match(new RegExp(`(\\d+)\\s*(?:-|–|to|se)\\s*(\\d+)\\s*${CLASS_NOUN}`, 'i'));
  if (range) {
    const low = clampInt(range[1], MIN_CLASSES, MAX_CLASSES, MIN_CLASSES);
    const high = clampInt(range[2], MIN_CLASSES, MAX_CLASSES, MAX_CLASSES);
    maxClasses = Math.max(low, high);
  } else {
    // A bare digit, or a number word — but only when a class noun follows, so
    // "do this" cannot be read as the Hindi "do" (2).
    const digits = lower.match(new RegExp(`(\\d+)\\s*${CLASS_NOUN}`, 'i'));
    if (digits) {
      numClasses = clampInt(digits[1], MIN_CLASSES, MAX_CLASSES, null);
    } else {
      const words = lower.match(new RegExp(`\\b(${Object.keys(NUMBER_WORDS).join('|')})\\s*${CLASS_NOUN}`, 'i'));
      if (words) numClasses = clampInt(NUMBER_WORDS[words[1].toLowerCase()], MIN_CLASSES, MAX_CLASSES, null);
    }
  }

  // "split into cats and dogs" — explicit names, only when no count was given.
  let classNames = null;
  if (numClasses === null && maxClasses === null) {
    const tail = text.match(/\b(?:into|as|in to|me|mein)\b\s+(.+)$/i);
    if (tail) {
      const parts = tail[1]
        .split(/\s*(?:,|\band\b|\baur\b|&|\/)\s*/i)
        .map((part) => part.replace(/\b(?:classes|class|groups?|buckets?|categories|category)\b/gi, ''))
        .map((part) => cleanName(part))
        .filter((part) => part && !/^\d+$/.test(part) && part.length <= MAX_NAME_CHARS);
      if (parts.length >= MIN_CLASSES && parts.length <= MAX_CLASSES) {
        classNames = uniqueClassNames(parts);
        numClasses = classNames.length;
      }
    }
  }

  const projectMatch = text.match(/\bproject\s+(?:called|named|")?\s*([\w][\w -]{0,58})/i);
  const modelMatch = text.match(/\bmodel\s+(?:called|named|")?\s*([\w][\w -]{0,58})/i);

  return {
    wantsBuild: true,
    wantsDivide,
    numClasses,
    maxClasses,
    classNames,
    projectName: projectMatch ? cleanName(projectMatch[1]) : null,
    modelName: modelMatch ? cleanName(modelMatch[1]) : null,
  };
};

/** "cats-and-dogs.zip" -> "cats and dogs" */
export const nameFromFile = (fileName) => {
  const stem = String(fileName || '').replace(/\.[^.]*$/, '').replace(/[_-]+/g, ' ');
  return cleanName(stem, 'Imported dataset');
};

/**
 * The plan the user confirms and the runner executes.
 *
 * `grouping.mode` decides the whole shape of the run:
 * - `folders`    the zip already says what the classes are; no model involved.
 * - `cluster`    ask /cluster/auto to group the images, then name the groups.
 * - `unlabeled`  import everything as unstructured and let the user label it.
 */
export const buildBasePlan = (scan, instruction, options = {}) => {
  const intent = parseIntent(instruction);
  if (!intent || !intent.wantsBuild) return null;
  if (!scan || scan.layout === 'empty' || !scan.totalImages) return null;

  const fallbackName = nameFromFile(options.fileName);
  const groups = scan.groups || [];
  const hasFolders = scan.layout === 'folder_per_class' && groups.length >= MIN_CLASSES;

  /*
   * A detection archive short-circuits everything below.
   *
   * The classes are whatever the annotations say — not the folders, and not a
   * clustering. Asking the user how many classes to make would be absurd when
   * the file already states them.
   */
  const meta = options.meta || null;
  const annotationFormat = meta && meta.format;
  if (annotationFormat === 'yolo' || annotationFormat === 'coco') {
    const names = uniqueClassNames(
      (meta.names || []).length ? meta.names : ['class 1'],
    );
    const willImportAll = Math.min(scan.totalImages, MAX_IMPORT_IMAGES);
    return {
      version: PLAN_VERSION,
      action: 'build_detection',
      target: {
        projectName: intent.projectName || fallbackName,
        projectId: options.projectId || null,
        modelName: intent.modelName || `${fallbackName} detector`,
        modelType: 'bounding_box',
      },
      grouping: { mode: 'annotations', numClasses: names.length, maxClasses: null },
      classes: names.map((name, index) => ({
        // Ids start at 1: the classes ARRAY ORDER is what becomes the YOLO
        // index at training time, so `id` is only a join key — and a 0 id is
        // falsy in JavaScript, which the labeling screens trip over.
        id: FIRST_CLASS_ID + index,
        name,
        count: null,
        source: String(index),
      })),
      limits: {
        totalImages: scan.totalImages,
        willImport: willImportAll,
        dropped: Math.max(0, scan.totalImages - willImportAll),
      },
      source: { fileName: options.fileName || null, layout: scan.layout, format: annotationFormat },
    };
  }

  // An explicit count that matches the folders is just a description of them.
  const askedCount = intent.numClasses;
  const explicitDivide =
    intent.wantsDivide || askedCount !== null || intent.maxClasses !== null || !!intent.classNames;
  const countMatchesFolders = askedCount !== null && askedCount === groups.length;

  let mode = 'unlabeled';
  if (hasFolders && (!explicitDivide || countMatchesFolders || (!askedCount && !intent.maxClasses && !intent.classNames))) {
    mode = 'folders';
  } else if (explicitDivide) {
    mode = 'cluster';
  } else if (hasFolders) {
    mode = 'folders';
  }

  let classes = [];
  let numClasses = null;
  let maxClasses = null;

  if (mode === 'folders') {
    const names = uniqueClassNames(groups.map((group) => group.name));
    classes = groups.map((group, index) => ({
      id: FIRST_CLASS_ID + index,
      name: names[index],
      count: group.count,
      source: group.name,
    }));
    numClasses = classes.length;
  } else if (mode === 'cluster') {
    numClasses = intent.classNames ? intent.classNames.length : askedCount;
    maxClasses = intent.maxClasses;
    const names = intent.classNames
      ? uniqueClassNames(intent.classNames)
      : Array.from({ length: numClasses || 0 }, (_, index) => `class ${index + 1}`);
    classes = names.map((name, index) => ({
      id: FIRST_CLASS_ID + index,
      name,
      count: null,
      source: null,
    }));
  }

  const willImport = Math.min(scan.totalImages, MAX_IMPORT_IMAGES);

  return {
    version: PLAN_VERSION,
    action: 'build_classification',
    target: {
      projectName: intent.projectName || fallbackName,
      projectId: options.projectId || null,
      modelName: intent.modelName || `${fallbackName} classifier`,
      modelType: 'classification',
    },
    grouping: { mode, numClasses, maxClasses },
    classes,
    limits: {
      totalImages: scan.totalImages,
      willImport,
      dropped: Math.max(0, scan.totalImages - willImport),
    },
    source: { fileName: options.fileName || null, layout: scan.layout, format: null },
  };
};

/**
 * Clamp every field of a plan that came from somewhere less trustworthy than
 * this file — i.e. the model.
 *
 * `base` is the browser-computed plan and supplies every fallback, so a field
 * the refinement omitted or mangled keeps the deterministic value. This is the
 * only door the model's output comes through.
 */
export const coercePlan = (raw, base) => {
  const safeBase = base || {};
  const input = raw && typeof raw === 'object' ? raw : {};
  const baseTarget = safeBase.target || {};
  const baseGrouping = safeBase.grouping || {};
  const inTarget = input.target && typeof input.target === 'object' ? input.target : {};

  /*
   * The grouping mode is the browser's, not the model's.
   *
   * It is derived from the archive's own layout plus a deterministic read of
   * the instruction, and it decides how many classes exist and how every image
   * is assigned. A model allowed to flip `folders` to `cluster` is not refining
   * a name, it is re-deciding what the import DOES — and it would do it with
   * none of the evidence, having never seen the file.
   */
  const mode = ['folders', 'cluster', 'unlabeled', 'annotations'].includes(baseGrouping.mode)
    ? baseGrouping.mode
    : 'unlabeled';

  // The model type follows the ARCHIVE, never the language model: it is decided
  // by whether the file carried annotations. Letting it be refined would point
  // an annotated dataset at save_classification_data, which would drop every box.
  const modelType = SUPPORTED_MODEL_TYPES.includes(baseTarget.modelType)
    ? baseTarget.modelType
    : 'classification';

  const totalImages = (safeBase.limits && safeBase.limits.totalImages) || 0;
  const classCeiling = Math.max(MIN_CLASSES, Math.min(MAX_CLASSES, totalImages || MAX_CLASSES));

  let numClasses = null;
  if (mode === 'annotations') {
    // The annotations state the classes. Nothing may change how many there are.
    numClasses = (Array.isArray(safeBase.classes) && safeBase.classes.length) || null;
  } else if (mode === 'folders') {
    // The folders ARE the classes. Nothing the model says can change how many
    // there are, only what they are called.
    numClasses = (Array.isArray(safeBase.classes) && safeBase.classes.length) || null;
  } else if (mode === 'cluster') {
    // Clamped from the BASE only. How many classes to make is either what the
    // user asked for or what the folders say; the model is not consulted.
    // A base value that cannot be read (NaN, a word) leaves it null, which
    // means "let the clustering pick k".
    numClasses = clampInt(baseGrouping.numClasses, MIN_CLASSES, classCeiling, null);
  }

  let maxClasses = null;
  if (mode === 'cluster' && numClasses == null) {
    maxClasses = clampInt(baseGrouping.maxClasses, MIN_CLASSES, classCeiling, null);
  }

  // Class names may be refined; ids, counts and the count itself may not.
  const baseClasses = Array.isArray(safeBase.classes) ? safeBase.classes : [];
  const wanted =
    mode === 'folders' || mode === 'annotations'
      ? baseClasses.length
      : numClasses || baseClasses.length || 0;
  const incoming = Array.isArray(input.classes) ? input.classes : [];
  const merged = [];
  for (let index = 0; index < wanted; index += 1) {
    const fromBase = baseClasses[index] || {};
    const fromModel = incoming[index] && typeof incoming[index] === 'object' ? incoming[index] : {};
    const refined = typeof fromModel.name === 'string' && fromModel.name.trim() ? fromModel.name : null;
    merged.push({
      id: FIRST_CLASS_ID + index,
      name: refined != null ? refined : fromBase.name,
      count: fromBase.count != null ? fromBase.count : null,
      source: fromBase.source != null ? fromBase.source : null,
    });
  }
  const names = uniqueClassNames(merged.map((entry, index) => entry.name || `class ${index + 1}`));
  merged.forEach((entry, index) => {
    entry.name = names[index];
  });

  return {
    version: PLAN_VERSION,
    action: modelType === 'bounding_box' ? 'build_detection' : 'build_classification',
    target: {
      projectName: cleanName(inTarget.projectName, baseTarget.projectName || 'Imported dataset'),
      // A project id is an identity, never a suggestion — the model cannot set it.
      projectId: baseTarget.projectId || null,
      modelName: cleanName(inTarget.modelName, baseTarget.modelName || 'Imported model'),
      modelType,
    },
    grouping: { mode, numClasses, maxClasses },
    classes: merged,
    limits: safeBase.limits || { totalImages: 0, willImport: 0, dropped: 0 },
    source: safeBase.source || { fileName: null, layout: 'flat', format: null },
    summary: typeof input.summary === 'string' ? cleanName(input.summary.slice(0, 400), '') : '',
  };
};

/**
 * A markdown mirror of the plan, stored as the card message's `content`.
 *
 * Not decoration. Four separate things read `content` and only `content`:
 * `toHistory` drops a message whose trimmed content is empty (so the model
 * would never learn the build happened), sidebar search matches on it,
 * `deriveTitle` names the thread from it, and the Markdown export prints
 * `_(no content)_` without it. A card that renders beautifully and stores an
 * empty string is invisible to all four.
 */
export const describePlan = (plan, t) => {
  if (!plan) return '';
  const { target, grouping, classes, limits } = plan;
  const names = (classes || []).map((entry) => entry.name).filter(Boolean);
  const lines = [];

  const head = {
    folders: 'assistant.build.mirror.folders',
    cluster: 'assistant.build.mirror.cluster',
    unlabeled: 'assistant.build.mirror.unlabeled',
    annotations: 'assistant.build.mirror.annotations',
  }[grouping && grouping.mode] || 'assistant.build.mirror.unlabeled';

  lines.push(
    t(head, {
      model: target.modelName,
      project: target.projectName,
      images: limits.willImport,
      classes: grouping.numClasses || names.length || 0,
    }),
  );

  if (names.length) lines.push(t('assistant.build.mirror.classes', { names: names.join(', ') }));
  if (limits.dropped > 0) {
    lines.push(t('assistant.build.mirror.capped', { dropped: limits.dropped, total: limits.totalImages }));
  }
  // Never return an empty string — see the note above.
  return lines.filter(Boolean).join('\n\n') || t('assistant.build.mirror.unlabeled', {
    model: target.modelName, project: target.projectName, images: limits.willImport, classes: 0,
  });
};

/** The same mirror, once the run has finished. */
export const describeRun = (plan, run, t) => {
  const base = describePlan(plan, t);
  if (!run || !run.status) return base;
  const tail = {
    done: 'assistant.build.mirror.done',
    partial: 'assistant.build.mirror.partial',
    failed: 'assistant.build.mirror.failed',
    cancelled: 'assistant.build.mirror.cancelled',
  }[run.status];
  if (!tail) return base;
  return [
    base,
    t(tail, {
      project: (run.projectName || (plan && plan.target.projectName)) || '',
      model: (run.modelName || (plan && plan.target.modelName)) || '',
      imported: run.imported != null ? run.imported : 0,
      expected: run.expected != null ? run.expected : 0,
      reason: run.error || '',
    }),
  ].join('\n\n');
};
